import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bioLinkService } from '../../../utils/storage';
import { useToast } from '../../../hooks/useToast';
import { useSubscription } from '../../../hooks/useSubscription';

interface BioLinkModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface Link {
  title: string;
  url: string;
}

export default function BioLinkModal({ onClose, onSuccess }: BioLinkModalProps) {
  const { t } = useTranslation('dashboard');
  const { showToast } = useToast();
  const { canCreateBio } = useSubscription();
  const [username, setUsername] = useState('');
  const [title, setTitle] = useState('');
  const [bio, setBio] = useState('');
  const [avatar, setAvatar] = useState('');
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [accentColor, setAccentColor] = useState('#14B8A6');
  const [links, setLinks] = useState<Link[]>([{ title: '', url: '' }]);
  const [loading, setLoading] = useState(false);

  const addLink = () => {
    setLinks([...links, { title: '', url: '' }]);
  };

  const updateLink = (index: number, field: 'title' | 'url', value: string) => {
    const newLinks = [...links];
    newLinks[index][field] = value;
    setLinks(newLinks);
  };

  const removeLink = (index: number) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!username || !title) {
      showToast('사용자명과 제목을 입력해주세요', 'error');
      return;
    }

    if (!canCreateBio()) {
      showToast('바이오 링크 생성 한도에 도달했습니다. 프로 플랜으로 업그레이드하세요.', 'error');
      return;
    }

    const validLinks = links.filter(link => link.title && link.url);
    if (validLinks.length === 0) {
      showToast('최소 1개의 링크를 추가해주세요', 'error');
      return;
    }

    setLoading(true);
    try {
      await bioLinkService.create({
        username,
        title,
        bio,
        avatar_url: avatar,
        links: validLinks,
        theme,
      });

      showToast('바이오 링크가 생성되었습니다', 'success');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('바이오 링크 저장 실패:', error);
      showToast(error.message || '바이오 링크 생성에 실패했습니다', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">{t('bio.create')}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('bio.username')}
                </label>
                <div className="flex items-center">
                  <span className="px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-gray-600 text-sm">
                    @
                  </span>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="yourname"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('bio.title')}
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="크리에이터 | 디자이너"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('bio.bio')}
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="자기소개를 입력하세요"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('bio.avatar')} URL
                </label>
                <input
                  type="url"
                  value={avatar}
                  onChange={(e) => setAvatar(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="https://example.com/avatar.jpg"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('bio.theme')}
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTheme('light')}
                      className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors whitespace-nowrap cursor-pointer ${
                        theme === 'light'
                          ? 'border-teal-500 bg-teal-50 text-teal-700'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {t('bio.light')}
                    </button>
                    <button
                      onClick={() => setTheme('dark')}
                      className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors whitespace-nowrap cursor-pointer ${
                        theme === 'dark'
                          ? 'border-teal-500 bg-teal-50 text-teal-700'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {t('bio.dark')}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('bio.accentColor')}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    링크
                  </label>
                  <button
                    onClick={addLink}
                    className="text-teal-600 hover:text-teal-700 text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line mr-1"></i>
                    {t('bio.addLink')}
                  </button>
                </div>
                <div className="space-y-3">
                  {links.map((link, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={link.title}
                        onChange={(e) => updateLink(index, 'title', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder={t('bio.linkTitle')}
                      />
                      <input
                        type="url"
                        value={link.url}
                        onChange={(e) => updateLink(index, 'url', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder={t('bio.linkUrl')}
                      />
                      {links.length > 1 && (
                        <button
                          onClick={() => removeLink(index)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('bio.preview')}
              </label>
              <div className="border-2 border-gray-300 rounded-2xl overflow-hidden">
                <div
                  className={`min-h-[600px] ${
                    theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                  }`}
                >
                  <div className="p-8 text-center">
                    <div className={`w-24 h-24 rounded-full mx-auto mb-4 ${
                      avatar ? '' : theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    } overflow-hidden`}>
                      {avatar && (
                        <img src={avatar} alt={username} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <h2 className={`text-xl font-bold mb-1 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      @{username || 'username'}
                    </h2>
                    <p className={`text-sm mb-3 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {title || '제목을 입력하세요'}
                    </p>
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      {bio || '소개를 입력하세요'}
                    </p>

                    <div className="mt-6 space-y-3">
                      {links.filter(link => link.title).map((link, index) => (
                        <a
                          key={index}
                          href={link.url}
                          className={`block w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                            theme === 'dark'
                              ? 'bg-gray-800 text-white hover:bg-gray-700'
                              : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                          }`}
                          style={{
                            borderColor: accentColor,
                            borderWidth: '2px',
                          }}
                        >
                          {link.title}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {t('action.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <i className="ri-loader-4-line animate-spin"></i>}
              {loading ? '생성 중...' : t('action.create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
